import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft } from 'react-icons/fa';
import SEO from '../features/seo/seo.feature';
import { analytics } from "../features/firebase/firebase-config";

const CookiePolicy: React.FC = () => {
  analytics.logEvent('page_view', {
    page_title: 'cookie policy',
  });

  // Define structured data for better SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Cookie Policy for PrepBestie",
    "description": "Detailed information about how PrepBestie uses cookies and similar technologies on our platform",
    "image": "https://prepbestie.com/prepbestie.png",
    "author": {
      "@type": "Organization",
      "name": "PrepBestie",
      "url": "https://prepbestie.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PrepBestie",
      "logo": {
        "@type": "ImageObject",
        "url": "https://prepbestie.com/prepbestie.png"
      }
    },
    "datePublished": "2024-01-01",
    "dateModified": new Date().toISOString().split('T')[0],
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://prepbestie.com/cookies"
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <SEO
        title='Cookie Policy | PrepBestie'
        description='PrepBestie Cookie Policy - Learn about our use of cookies, how they work, what types we use, and how to manage your cookie preferences for an optimal user experience.'
        name='PrepBestie'
        type='article'
        structuredData={structuredData}
        keywords={[
          'Prep Bestie',
          'PrepBestie',
          'Cookie Policy',
          'Website cookies',
          'Cookie preferences',
          'Privacy settings',
          'Essential cookies',
          'Analytics cookies',
          'Cookie consent',
          'Cookie management',
          'Browser cookies',
          'GDPR cookies',
          'Cookie tracking',
          'Data privacy',
          'Website tracking'
        ]}
      />

      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900">Cookie Policy</h1>
            <Link
              to="/"
              className="flex items-center text-indigo-600 hover:text-indigo-800 transition"
            >
              <FaArrowLeft className="mr-2" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <motion.div
          className="bg-white shadow-md rounded-xl p-8 mb-8 cookie-policy-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">Cookie Policy for PrepBestie</h2>
          <p className="text-gray-600 mb-4"><strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">1. Introduction</h3>
            <p className="text-gray-600 mb-4">
              PrepBestie ("us", "we", or "our") uses cookies and similar technologies on our website and application. This Cookie Policy explains how we use cookies, what types we use, and how you can control them.
            </p>
            <p className="text-gray-600 mb-4">
              By using PrepBestie, you consent to the use of cookies in accordance with this Cookie Policy. If you do not agree with our use of cookies, please adjust your browser settings or discontinue use of our services.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">2. What Are Cookies?</h3>
            <p className="text-gray-600 mb-4">
              Cookies are small text files that are stored on your device (computer, tablet, or mobile phone) when you visit a website. They are widely used to make websites work more efficiently, provide a better user experience, and give website owners information about how their site is used.
            </p>
            <p className="text-gray-600 mb-4">
              Cookies can be "persistent" or "session" cookies. Persistent cookies remain on your device when you go offline, while session cookies are deleted as soon as you close your web browser.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">3. How We Use Cookies</h3>
            <p className="text-gray-600 mb-4">
              We use cookies for several purposes, including:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li><strong>Essential Cookies:</strong> These are necessary for the website to function properly. They enable core functionality such as security, network management, and account access.</li>
              <li><strong>Functionality Cookies:</strong> These help us remember your preferences and settings to enhance your experience (e.g., language preferences, interface customizations).</li>
              <li><strong>Analytics Cookies:</strong> We use these to understand how visitors interact with our website, which pages are visited most often, and to detect and resolve errors. This helps us improve our service.</li>
              <li><strong>Performance Cookies:</strong> These help us count visits and traffic sources to measure and improve the performance of our site.</li>
              <li><strong>Marketing Cookies:</strong> These may be set through our site by our advertising partners. They may be used to build a profile of your interests to show you relevant advertisements on other sites.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">4. Specific Cookies We Use</h3>

            <h4 className="text-lg font-medium text-gray-800 mb-2">4.1 Strictly Necessary Cookies</h4>
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border border-gray-200 mb-4">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 border text-left text-sm font-medium text-gray-700">Cookie Name</th>
                    <th className="px-4 py-2 border text-left text-sm font-medium text-gray-700">Purpose</th>
                    <th className="px-4 py-2 border text-left text-sm font-medium text-gray-700">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-2 border text-sm text-gray-600">auth_session</td>
                    <td className="px-4 py-2 border text-sm text-gray-600">Used to maintain your authenticated session</td>
                    <td className="px-4 py-2 border text-sm text-gray-600">Session</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border text-sm text-gray-600">csrf_token</td>
                    <td className="px-4 py-2 border text-sm text-gray-600">Security token to prevent cross-site request forgery</td>
                    <td className="px-4 py-2 border text-sm text-gray-600">Session</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4 className="text-lg font-medium text-gray-800 mb-2">4.2 Analytics Cookies</h4>
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border border-gray-200 mb-4">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 border text-left text-sm font-medium text-gray-700">Cookie Name</th>
                    <th className="px-4 py-2 border text-left text-sm font-medium text-gray-700">Purpose</th>
                    <th className="px-4 py-2 border text-left text-sm font-medium text-gray-700">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-2 border text-sm text-gray-600">_ga</td>
                    <td className="px-4 py-2 border text-sm text-gray-600">Used by Google Analytics to distinguish users</td>
                    <td className="px-4 py-2 border text-sm text-gray-600">2 years</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border text-sm text-gray-600">_gid</td>
                    <td className="px-4 py-2 border text-sm text-gray-600">Used by Google Analytics to distinguish users</td>
                    <td className="px-4 py-2 border text-sm text-gray-600">24 hours</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border text-sm text-gray-600">_gat</td>
                    <td className="px-4 py-2 border text-sm text-gray-600">Used by Google Analytics to throttle request rate</td>
                    <td className="px-4 py-2 border text-sm text-gray-600">1 minute</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border text-sm text-gray-600">firebase_analytics</td>
                    <td className="px-4 py-2 border text-sm text-gray-600">Used for Firebase analytics to improve user experience</td>
                    <td className="px-4 py-2 border text-sm text-gray-600">2 years</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4 className="text-lg font-medium text-gray-800 mb-2">4.3 Functionality Cookies</h4>
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border border-gray-200 mb-4">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 border text-left text-sm font-medium text-gray-700">Cookie Name</th>
                    <th className="px-4 py-2 border text-left text-sm font-medium text-gray-700">Purpose</th>
                    <th className="px-4 py-2 border text-left text-sm font-medium text-gray-700">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-2 border text-sm text-gray-600">lang</td>
                    <td className="px-4 py-2 border text-sm text-gray-600">Remembers the user's selected language</td>
                    <td className="px-4 py-2 border text-sm text-gray-600">1 year</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border text-sm text-gray-600">theme</td>
                    <td className="px-4 py-2 border text-sm text-gray-600">Remembers the user's UI theme preference</td>
                    <td className="px-4 py-2 border text-sm text-gray-600">1 year</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">5. Third-Party Cookies</h3>
            <p className="text-gray-600 mb-4">
              We also use services from third parties who may set cookies on your device when you visit our site. These include:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li><strong>Google Analytics:</strong> For website analytics and performance tracking</li>
              <li><strong>Firebase:</strong> For analytics and app performance monitoring</li>
              <li><strong>Stripe:</strong> For payment processing, if you subscribe to our services</li>
              <li><strong>Cloudinary:</strong> For media content delivery</li>
            </ul>
            <p className="text-gray-600 mb-4">
              These third parties may use cookies, web beacons, and similar technologies to collect or receive information from our website and elsewhere on the internet and use that information to provide measurement services and target ads.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">6. Managing Cookies</h3>
            <p className="text-gray-600 mb-4">
              Most web browsers allow you to manage your cookie preferences. You can:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Delete cookies from your device</li>
              <li>Block cookies by activating the setting on your browser that allows you to refuse all or some cookies</li>
              <li>Set your browser to notify you when you receive a cookie</li>
            </ul>
            <p className="text-gray-600 mb-4">
              Please note that if you block or delete cookies used by our website, you may not be able to use all the features and functions of our services.
            </p>
            <p className="text-gray-600 mb-4">
              For more information about how to manage cookies in your browser, please visit:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 transition">Google Chrome</a></li>
              <li><a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 transition">Mozilla Firefox</a></li>
              <li><a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 transition">Safari</a></li>
              <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 transition">Microsoft Edge</a></li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">7. Cookie Consent</h3>
            <p className="text-gray-600 mb-4">
              When you first visit our website, you may see a cookie banner that allows you to accept or decline non-essential cookies. You can change your preferences at any time by clicking on the "Cookie Settings" link in our footer.
            </p>
            <p className="text-gray-600 mb-4">
              By using our website, you consent to our use of cookies as described in this Cookie Policy. If you do not wish to accept cookies from our website, you should either: (i) stop using our website; or (ii) delete and block all cookies from this website using your browser settings.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">8. Changes to Our Cookie Policy</h3>
            <p className="text-gray-600 mb-4">
              We may update our Cookie Policy from time to time. Any changes we make will be posted on this page and, where appropriate, notified to you by email or when you next visit our website. We encourage you to review this Cookie Policy periodically to stay informed about our use of cookies.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-semibold text-gray-800 mb-4">9. Contact Us</h3>
            <p className="text-gray-600 mb-4">
              If you have any questions about our use of cookies or this Cookie Policy, please contact us at:
            </p>
            <p className="text-gray-600 mb-4">
              <a href="mailto:privacy@prepbestie.com" className="text-indigo-600 hover:text-indigo-800 transition">privacy@prepbestie.com</a>
            </p>
          </section>
        </motion.div>
      </main>

      <footer className="bg-gray-100 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-500 text-sm">
          &copy; {new Date().getFullYear()} PrepBestie. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default CookiePolicy; 